import { useQuery } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { client, unwrap, type Schemas } from '../api/client';
import { playChime, showBrowserNotification } from '../lib/browser-notifications';
import { formatDateTime } from '../lib/format';
import { useAction } from '../lib/hooks';
import { useRealtimeListener } from '../lib/realtime';
import { Button, useToast } from '../ui/ui';
import { Icon } from '../ui/icons';

type Notification = Schemas['NotificationListDto']['data'][number];
const BASE_TITLE = 'EATHERIX';

/**
 * Campana de la barra superior: cuenta de no leídas (también en el título de la pestaña), lista
 * desplegable y, al llegar una notificación en tiempo real, aviso en el panel, notificación del
 * navegador y sonido según las preferencias de la persona.
 */
export function NotificationBell({ title = BASE_TITLE }: { title?: string }) {
  const navigate = useNavigate();
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  const list = useQuery({
    queryKey: ['notifications'],
    queryFn: () => unwrap(client.GET('/v1/notifications', { params: { query: { limit: 20 } } })),
    // Respaldo si el tiempo real no está disponible.
    refetchInterval: 60_000,
  });
  const unread = list.data?.unreadCount ?? 0;
  // La pestaña lleva el producto contratado (EATHERIX CRM, ERP o Sales).
  const baseTitle = title;
  const invalidate = [['notifications']];
  const markRead = useAction(
    (id: string) =>
      unwrap(client.POST('/v1/notifications/{id}/read', { params: { path: { id } } })),
    { invalidate },
  );
  const markAll = useAction(() => unwrap(client.POST('/v1/notifications/read-all')), {
    invalidate,
  });

  useEffect(() => {
    document.title = unread > 0 ? `(${unread > 99 ? '99+' : unread}) ${baseTitle}` : baseTitle;
    return () => {
      document.title = BASE_TITLE;
    };
  }, [unread, baseTitle]);

  useRealtimeListener((event, data) => {
    if (event !== 'notification.created') return;
    const notification = data.notification as Notification | undefined;
    const delivery = (data.delivery ?? {}) as {
      toast?: boolean;
      browser?: boolean;
      sound?: boolean;
    };
    if (!notification) return;
    const text = notification.body
      ? `${notification.title}: ${notification.body}`
      : notification.title;
    if (delivery.toast) toast.notify(text);
    if (delivery.browser)
      showBrowserNotification(notification.title, notification.body, {
        tag: notification.id,
        onClick: () => notification.link && void navigate(notification.link),
      });
    if (delivery.sound) playChime();
  });

  useEffect(() => {
    if (!open) return;
    const onPointer = (e: PointerEvent) => {
      if (!root.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('pointerdown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const openNotification = (notification: Notification) => {
    if (!notification.read) markRead.mutate(notification.id);
    setOpen(false);
    if (notification.link) void navigate(notification.link);
  };

  const items = list.data?.data ?? [];
  return (
    <div className="notif" ref={root}>
      <button
        type="button"
        className="btn btn-sm notif-bell"
        aria-label={unread > 0 ? `Notificaciones, ${unread} sin leer` : 'Notificaciones'}
        aria-expanded={open}
        aria-haspopup="dialog"
        title="Notificaciones"
        onClick={() => setOpen((v) => !v)}
      >
        <Icon name="bell" size={16} />
        {unread > 0 && <span className="notif-count">{unread > 99 ? '99+' : unread}</span>}
      </button>
      {open && (
        <div className="notif-panel" role="dialog" aria-label="Notificaciones">
          <header>
            <strong>Notificaciones</strong>
            <Button
              size="sm"
              variant="ghost"
              disabled={unread === 0}
              busy={markAll.isPending}
              onClick={() => markAll.mutate(undefined)}
            >
              Marcar todas como leídas
            </Button>
          </header>
          {items.length === 0 ? (
            <p className="muted notif-empty">No tienes notificaciones.</p>
          ) : (
            <ul>
              {items.map((n) => (
                <li key={n.id}>
                  <button
                    type="button"
                    className={n.read ? 'notif-item' : 'notif-item notif-unread'}
                    onClick={() => openNotification(n)}
                  >
                    <span className="notif-title">
                      {n.title}
                      {n.count > 1 && <span className="muted"> ({n.count})</span>}
                    </span>
                    {n.body && <span className="notif-body">{n.body}</span>}
                    <span className="notif-time">{formatDateTime(n.createdAt)}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
          <footer>
            <Link to="/settings/notifications" onClick={() => setOpen(false)}>
              Preferencias de notificación
            </Link>
          </footer>
        </div>
      )}
    </div>
  );
}
